/**
 * PriorityIcon — a small Linear-style "signal bars" glyph for a task's
 * priority, meant to sit beside the StatusIcon in list/table rows. Four
 * bottom-aligned bars of increasing height in a single neutral tone; the
 * priority is encoded by ONE thing — how many bars are filled (low → 1,
 * urgent → 4). Empty bars render as a faint track so the staircase shape
 * stays legible. No per-priority color and no semantic tinting: the fill
 * count is the only metric, so every glyph reads on the same scale.
 *
 * Four bars (not three) so all four levels are visually distinct — the old
 * three-bar glyph lit all three for both high and urgent, leaving them
 * apart only by color. The hover `title` carries the label.
 */
import { cn } from "../../utils";
import { PRIORITY_LABEL, type TaskPriority } from "../../types/projects";

// How many of the four bars are filled per level. This is the whole signal.
const FILLED_BARS: Record<TaskPriority, number> = {
  low: 1,
  medium: 2,
  high: 3,
  urgent: 4,
};

// Bottom-aligned bars in a 14×14 box (matches StatusIcon's size-3.5).
const BARS = [
  { x: 0, height: 4 },
  { x: 4, height: 7 },
  { x: 8, height: 10 },
  { x: 12, height: 13 },
];
const BAR_WIDTH = 2;

export function PriorityIcon({
  priority,
  className,
}: {
  priority: TaskPriority;
  className?: string;
}) {
  const filled = FILLED_BARS[priority];
  return (
    <svg
      viewBox="0 0 14 14"
      aria-hidden="true"
      className={cn("size-3.5 shrink-0 text-foreground", className)}
    >
      <title>{`${PRIORITY_LABEL[priority]} priority`}</title>
      {BARS.map((bar, i) => (
        <rect
          key={bar.x}
          x={bar.x}
          y={14 - bar.height}
          width={BAR_WIDTH}
          height={bar.height}
          rx={1}
          fill="currentColor"
          opacity={i < filled ? 1 : 0.25}
        />
      ))}
    </svg>
  );
}
